import { type Component, type JSX } from 'solid-js'
import { Icon } from '@iconify-icon/solid'
import { cn } from '../../../shared/utils/cn'

const DEFAULT_TOOLTIP =
  'Toggle to offer extra perks like Sound Check, Meet and Greet, etc. Set the availability window and customize how these options appear to your fans.'

const InfoTooltip: Component<{ readonly text: string }> = (props) => (
  <span class="relative inline-flex shrink-0 cursor-help align-middle text-gray-600 group/tip">
    <Icon icon="heroicons:information-circle" class="text-[1rem]" aria-hidden />
    <span
      role="tooltip"
      class="pointer-events-none absolute left-[calc(100%+0.75rem)] top-1/2 z-50 w-max max-w-[min(20rem,calc(100vw-3rem))] -translate-y-1/2 opacity-0 transition-opacity duration-200 ease-out group-hover/tip:opacity-100"
    >
      <span class="relative block overflow-visible">
        <span
          aria-hidden
          class="absolute left-0 top-1/2 z-[1] flex h-4 w-[10px] -translate-x-full -translate-y-1/2 items-center justify-end overflow-visible"
        >
          <span class="absolute right-0 top-1/2 h-0 w-0 -translate-y-1/2 border-y-[8px] border-y-transparent border-r-[10px] border-r-gray-200/90 [filter:drop-shadow(0_3px_12px_rgba(15,23,42,0.2))]" />
          <span class="absolute right-px top-1/2 z-[1] h-0 w-0 -translate-y-1/2 border-y-[7px] border-y-transparent border-r-[9px] border-r-gray-50" />
        </span>
        <span class="relative z-[2] block rounded-xl border border-gray-200/90 bg-gray-50 px-5 py-4 text-left text-sm font-semibold leading-relaxed text-gray-900">
          {props.text}
        </span>
      </span>
    </span>
  </span>
)

export interface FoldableSectionProps {
  readonly id: string
  readonly title: string
  readonly tooltip?: string
  readonly enabled: () => boolean
  readonly onToggle: (enabled: boolean) => void
  readonly children: JSX.Element
}

const FoldableSection: Component<FoldableSectionProps> = (props) => {
  return (
    <div
      id={`section-${props.id}`}
      class={cn(
        'rounded-lg border transition-colors duration-200',
        props.enabled() ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200'
      )}
    >
      <button
        type="button"
        class="w-full px-6 py-5 flex items-center justify-between cursor-pointer"
        onClick={() => props.onToggle(!props.enabled())}
      >
        <div class="flex items-center gap-2">
          <h2
            class={cn(
              'text-xl font-bold transition-colors duration-200',
              props.enabled() ? 'text-gray-900' : 'text-gray-400'
            )}
          >
            {props.title}
          </h2>
          <InfoTooltip text={props.tooltip ?? DEFAULT_TOOLTIP} />
        </div>
        <div class="flex items-center gap-3">
          <div
            class={cn(
              'w-9 h-5 rounded-full relative transition-colors duration-200',
              props.enabled() ? 'bg-primary-600' : 'bg-gray-300'
            )}
          >
            <div
              class={cn(
                'absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200',
                props.enabled() ? 'translate-x-4' : 'translate-x-0.5'
              )}
            />
          </div>
          <span class={cn('text-gray-400 transition-transform duration-200', props.enabled() && 'rotate-180')}>
            <Icon icon="heroicons:chevron-down" class="text-[1.25rem]" />
          </span>
        </div>
      </button>
      <div
        class={cn(
          'grid transition-[grid-template-rows] duration-200 ease-in-out',
          props.enabled() ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div class="overflow-hidden">
          <div class="px-6 pb-5 flex flex-col gap-4">{props.children}</div>
        </div>
      </div>
    </div>
  )
}

export default FoldableSection
